import { useCallback, useMemo, useRef, useState } from 'react'
import { VirtuosoGrid } from 'react-virtuoso'
import { times } from 'lodash'

import { ArrowBack, AssignmentOutlined, FolderOutlined, FolderSharedOutlined } from '@mui/icons-material'

import {
  CardSkeleton,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  SearchTextField,
  Stack,
  Folder,
  Placeholder,
  Breadcrumbs,
  Header3,
  useDebouncedValue,
} from '@collaborate-health/uikit'
import { useSelector } from '@tabeeb/store'
import { getCurrentUserId } from '@tabeeb/modules/account/selectors'
import { useFormTitleFormatter } from '@tabeeb/modules/shared/utils/text'
import { useFormFolders } from '@tabeeb/modules/formsPage/hooks'

import AvailableFormsItem from '../AvailableFormsItem'
import { getContentForms } from '../../selectors'
import { useAvailableForms } from '../../hooks'

import { FormsGridRowsContainer, FormsGridItemContainer, FormsGridItemPlaceholder } from './AttachFormsDialog.styles'

export interface AttachFormsDialogProps {
  open: boolean
  onClose: () => void
}

const AttachFormsDialog: React.FC<AttachFormsDialogProps> = ({ open, onClose }) => {
  const dialogContentRef = useRef<HTMLDivElement>(null)
  const [searchText, setSearchText] = useState('')
  const debouncedSearchText = useDebouncedValue(searchText, 500)

  const [currentFolderId, setCurrentFolderId] = useState<number | undefined>(undefined)

  const { f } = useFormTitleFormatter()

  const currentUserId = useSelector<number>(getCurrentUserId)

  const { loading: loadingForms, forms } = useAvailableForms({
    folderId: currentFolderId,
    enabled: open,
  })

  const { formFolders: foldersList } = useFormFolders({ enabled: open })

  const currentFolder = useMemo(() => {
    return (foldersList ?? []).find((folder) => folder.FolderId === currentFolderId)
  }, [foldersList, currentFolderId])

  const onExited = useCallback(() => {
    setCurrentFolderId(undefined)
    setSearchText('')
  }, [])

  const isAttachDetachProcessing = useSelector((state) => state.forms.attachDetachProcessingFormId)
  const attachedFormsIds = useSelector((state) => new Set(getContentForms(state).map((form) => form.FormId)))

  const filteredFormsList =
    forms &&
    debouncedSearchText &&
    forms.filter((item) => item.Name!.toLowerCase().indexOf(debouncedSearchText.toLowerCase()) > -1)

  const formsList = filteredFormsList || forms

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='lg'
      fullWidth
      TransitionProps={{
        unmountOnExit: true,
        onExited,
      }}
    >
      <DialogTitle>
        <Stack alignItems='center' direction='row' spacing={2}>
          {currentFolder && (
            <IconButton
              size='small'
              color='primary'
              variant='outlined'
              shape='rectangular'
              title='Click to exit the folder'
              onClick={() => {
                setCurrentFolderId(undefined)
              }}
            >
              <ArrowBack />
            </IconButton>
          )}

          <Breadcrumbs>
            <Header3>{f('Available forms')}</Header3>

            {currentFolder && <Header3>{currentFolder.Name}</Header3>}
          </Breadcrumbs>
        </Stack>
      </DialogTitle>
      <DialogContent ref={dialogContentRef}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <SearchTextField
              value={searchText}
              placeholder='Search...'
              fullWidth
              onChange={(event) => {
                const { value } = event.target
                setSearchText(value)
              }}
            />
          </Grid>

          {loadingForms ? (
            <Grid item container spacing={2}>
              {times(8, (index) => (
                <Grid key={index} item xs={12} sm={6} md={4} lg={3} xl={3}>
                  <CardSkeleton />
                </Grid>
              ))}
            </Grid>
          ) : (
            <>
              {!currentFolder && (
                <Grid item container spacing={2}>
                  {(foldersList ?? []).map((folder) => (
                    <Grid key={folder.FolderId} item xs={12} sm={6} md={4} lg={3} xl={3}>
                      <Folder
                        icon={
                          folder.UserId !== currentUserId ? (
                            <FolderSharedOutlined color='action' />
                          ) : (
                            <FolderOutlined color='action' />
                          )
                        }
                        title={folder.Name!}
                        onClick={() => {
                          setCurrentFolderId(folder.FolderId)
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              )}

              {formsList.length > 0 ? (
                <Grid item xs={12}>
                  <VirtuosoGrid
                    customScrollParent={dialogContentRef.current ?? undefined}
                    totalCount={formsList.length}
                    components={{
                      List: FormsGridRowsContainer,
                      Item: FormsGridItemContainer,
                      ScrollSeekPlaceholder: FormsGridItemPlaceholder,
                    }}
                    scrollSeekConfiguration={{
                      enter: (velocity) => Math.abs(velocity) > 1000,
                      exit: (velocity) => Math.abs(velocity) < 30,
                    }}
                    // eslint-disable-next-line react/no-unstable-nested-components
                    itemContent={(index) => {
                      const form = formsList[index]

                      return (
                        <AvailableFormsItem
                          attached={attachedFormsIds.has(form.FormId)}
                          disabled={isAttachDetachProcessing === form.FormId}
                          form={form}
                        />
                      )
                    }}
                  />
                </Grid>
              ) : (
                <Grid item xs={12}>
                  <Placeholder icon={AssignmentOutlined} title={f(`No forms found`)} />
                </Grid>
              )}
            </>
          )}
        </Grid>
      </DialogContent>
    </Dialog>
  )
}

export default AttachFormsDialog

